import React, { useEffect, useState } from 'react';
import { Check, Loader2, MessageSquareReply, Trash2, X, ChevronDown, BookOpen, CheckCircle, Clock } from 'lucide-react';
import { GET, PUT, DELETE as DELETE_API } from '../../services/api';

const Toast = ({ message, type, onClose }) => {
  if (!message) return null;
  const bgColor = type === 'success' ? 'bg-emerald-600' : 'bg-red-500';
  return (
    <div className={`fixed bottom-6 right-6 px-6 py-3 rounded-xl shadow-2xl text-white font-bold flex items-center gap-3 z-[1000] animate-in slide-in-from-bottom-5 ${bgColor}`}>
      {type === 'success' ? <Check className="w-5 h-5" /> : <X className="w-5 h-5" />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-auto p-1 rounded-full hover:bg-white/20 transition-colors">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

const getQuestionList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.questions)) return payload.questions;
  if (Array.isArray(payload?.data?.questions)) return payload.data.questions;
  return [];
};

const getQuestionObject = (payload) => {
  if (!payload) return null;
  if (payload?.question) return payload.question;
  if (payload?.data?.question) return payload.data.question;
  if (payload?.data && !Array.isArray(payload.data)) return payload.data;
  return payload;
};

const getUnreadStatusCount = (item) => {
  if (!item?.status) return 'Pending';
  return item.status;
};

// Question Card Component
const QuestionCard = ({ item, onDelete, actionLoading, onAnswerUpdated, isExpanded, onToggleExpand }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [answerText, setAnswerText] = useState(item.answer || '');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isAnswered = item.answer && item.answer.trim() !== '';

  // Sync answerText when item.answer changes (from API update)
  useEffect(() => {
    setAnswerText(item.answer || '');
  }, [item.answer]);

  const handleAnswer = async () => {
    if (!answerText.trim()) {
      alert('Answer cannot be empty');
      return;
    }
    setIsSubmitting(true);
    try {
      await PUT(`/help-center/reply/${item._id || item.id}`, { answer: answerText.trim() });
      setIsEditing(false);
      onAnswerUpdated(); // Callback to refresh data
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-md transition-all duration-300">
      {/* Header / Question Section */}
      <div
        onClick={onToggleExpand}
        className="p-5 sm:p-6 cursor-pointer hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-start gap-4">
          {/* Member Avatar with Profile Pic */}
          <div className="flex-shrink-0">
            {item.profile_picture ? (
              <img
                src={item.profile_picture}
                alt={item.full_name}
                className="w-12 h-12 rounded-full object-cover border-2 border-slate-200"
              />
            ) : (
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-sm">
                {(item.full_name || item.fullName || 'U')
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .substring(0, 2)
                  .toUpperCase()}
              </div>
            )}
          </div>

          {/* Question Content */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <h3 className="font-semibold text-slate-800 text-sm">{item.full_name || item.fullName || 'Anonymous'}</h3>
                  <span
                    className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      isAnswered ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                    }`}
                  >
                    {isAnswered ? (
                      <>
                        <CheckCircle className="w-3.5 h-3.5" />
                        Answered
                      </>
                    ) : (
                      <>
                        <Clock className="w-3.5 h-3.5" />
                        Pending
                      </>
                    )}
                  </span>
                </div>

                {/* Member Details - Small */}
                <div className="text-xs text-slate-500 mb-2">
                  {item.city_name && item.chapter_name && (
                    <>
                      {item.city_name} • {item.chapter_name}
                    </>
                  )}
                </div>

                {/* Question Text */}
                <p className="text-sm text-slate-700 line-clamp-2">{item.question || 'No question provided'}</p>

                {/* Date */}
                <div className="text-xs text-slate-400 mt-2">
                  {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }) : 'N/A'}
                </div>
              </div>

              {/* Expand Chevron */}
              <div className="flex-shrink-0">
                <ChevronDown
                  className={`w-5 h-5 text-slate-400 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/50">
          {/* Full Member Info */}
          <div className="px-5 sm:px-6 py-4 border-b border-slate-100">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div>
                <label className="text-slate-500 font-semibold uppercase block mb-1">Name</label>
                <p className="text-slate-700">{item.full_name || item.fullName || '-'}</p>
              </div>
              <div>
                <label className="text-slate-500 font-semibold uppercase block mb-1">Email</label>
                <p className="text-slate-700 truncate">{item.email || '-'}</p>
              </div>
              <div>
                <label className="text-slate-500 font-semibold uppercase block mb-1">Phone</label>
                <p className="text-slate-700">{item.personal_phone || '-'}</p>
              </div>
              <div>
                <label className="text-slate-500 font-semibold uppercase block mb-1">Chapter</label>
                <p className="text-slate-700">{item.chapter_name || '-'}</p>
              </div>
            </div>
          </div>

          {/* Question */}
          <div className="px-5 sm:px-6 py-4 border-b border-slate-100">
            <label className="text-xs text-slate-500 font-semibold uppercase block mb-2">Question</label>
            <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{item.question || '-'}</p>
          </div>

          {/* Answer / Reply Section */}
          <div className="px-5 sm:px-6 py-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-500 font-semibold uppercase">Answer</label>
            </div>
            
            {isEditing ? (
              <div className="space-y-3">
                <textarea
                  rows={5}
                  value={answerText}
                  onChange={(e) => setAnswerText(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your answer..."
                />
                <div className="flex gap-2 justify-end">
                  <button
                    onClick={() => {
                      setIsEditing(false);
                      setAnswerText(item.answer || '');
                    }}
                    className="px-3 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-100 text-sm font-semibold"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAnswer}
                    disabled={isSubmitting}
                    className="px-3 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold flex items-center gap-2"
                  >
                    {isSubmitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                    {isAnswered ? 'Update Answer' : 'Submit Answer'}
                  </button>
                </div>
              </div>
            ) : item.answer ? (
              <p className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed bg-white p-3 rounded-lg border border-slate-200">
                {item.answer}
              </p>
            ) : (
              <>
                <p className="text-sm text-slate-400 italic mb-3">No answer provided yet</p>
                <button
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors font-semibold text-sm"
                >
                  <MessageSquareReply className="w-4 h-4" />
                  Reply Now
                </button>
              </>
            )}
          </div>

          {/* Delete & Edit Actions */}
          <div className="px-5 sm:px-6 py-4 flex justify-end gap-3">
            {isAnswered && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors font-semibold text-sm"
              >
                <MessageSquareReply className="w-4 h-4" />
                Edit Answer
              </button>
            )}
            <button
              type="button"
              onClick={() => onDelete(item._id || item.id)}
              disabled={actionLoading}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50 font-semibold text-sm"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const HelpCenterPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ message: '', type: '' });
  const [expandedId, setExpandedId] = useState(null); // Track which card is expanded
  const [filterStatus, setFilterStatus] = useState('pending'); // Default to 'pending'

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast({ message: '', type: '' }), 3000);
  };

  const fetchQuestions = async () => {
    setLoading(true);
    try {
      const data = await GET('/help-center/questions');
      setRows(getQuestionList(data));
      showToast('Questions refreshed', 'success');
    } catch (error) {
      console.error('Error fetching help center questions:', error);
      showToast('Failed to load help center questions', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchQuestions();
  }, []);

  const handleDelete = async (id) => {
    const confirmed = window.confirm('Are you sure you want to delete this question?');
    if (!confirmed) return;

    setActionLoading(true);
    try {
      await DELETE_API(`/help-center/questions/${id}`);
      showToast('Question deleted successfully', 'success');
      fetchQuestions();
    } catch (error) {
      console.error('Error deleting help center question:', error);
      showToast('Failed to delete question', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Filter questions
  const filteredRows = rows.filter((item) => {
    if (filterStatus === 'all') return true;
    const isAnswered = item.answer && item.answer.trim() !== '';
    if (filterStatus === 'answered') return isAnswered;
    if (filterStatus === 'pending') return !isAnswered;
    return true;
  });

  const answeredCount = rows.filter((item) => item.answer && item.answer.trim() !== '').length;
  const pendingCount = rows.length - answeredCount;

  return (
    <div>
      <div className="space-y-5">
        {/* Header */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Help Center</h1>
              <p className="text-sm text-slate-500">Manage member questions and provide answers</p>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-3 sm:gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 font-semibold uppercase mb-1">Total Questions</p>
              <p className="text-2xl font-bold text-slate-800">{rows.length}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 font-semibold uppercase mb-1">Answered</p>
              <p className="text-2xl font-bold text-emerald-600">{answeredCount}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-4">
              <p className="text-xs text-slate-500 font-semibold uppercase mb-1">Pending</p>
              <p className="text-2xl font-bold text-amber-600">{pendingCount}</p>
            </div>
          </div>
        </div>

        {/* Filter & Refresh */}
        <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-4">
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setFilterStatus('pending')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                filterStatus === 'pending'
                  ? 'bg-amber-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Pending ({pendingCount})
            </button>
            <button
              onClick={() => setFilterStatus('answered')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                filterStatus === 'answered'
                  ? 'bg-emerald-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              Answered ({answeredCount})
            </button>
            <button
              onClick={() => setFilterStatus('all')}
              className={`px-4 py-2 rounded-lg font-semibold text-sm transition-colors ${
                filterStatus === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              All ({rows.length})
            </button>
          </div>
          <button
            type="button"
            onClick={fetchQuestions}
            disabled={loading}
            className="px-4 py-2 rounded-lg border border-slate-200 text-slate-700 font-semibold hover:bg-slate-50 disabled:opacity-50 text-sm transition-colors"
          >
            Refresh
          </button>
        </div>

        {/* Questions Grid */}
        <div className="space-y-4">
          {loading ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center text-slate-500">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="w-8 h-8 animate-spin" />
                <span className="font-medium">Loading questions...</span>
              </div>
            </div>
          ) : filteredRows.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 p-12 flex items-center justify-center text-slate-400">
              <div className="text-center">
                <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p className="font-medium">
                  {rows.length === 0 ? 'No help center questions found.' : 'No questions match your filter.'}
                </p>
              </div>
            </div>
          ) : (
            filteredRows.map((item) => (
              <QuestionCard
                key={item._id || item.id}
                item={item}
                onDelete={handleDelete}
                actionLoading={actionLoading}
                onAnswerUpdated={fetchQuestions}
                isExpanded={expandedId === (item._id || item.id)}
                onToggleExpand={() => setExpandedId(expandedId === (item._id || item.id) ? null : (item._id || item.id))}
              />
            ))
          )}
        </div>
      </div>

      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: '', type: '' })}
        />
      )}
    </div>
  );
};

export default HelpCenterPage;
